<template>
  <div>
    <div class="d-flex" style="border-bottom: 1px solid var(--light-4);">
      <div class="d-flex flex-column">
        <span class="toolbar-top mb-2">
          Path: {{ (object.realpath || object.filepath).replace('root/', '').replaceAll('/', ' > ')}}
        </span>
        <div class="toolbar-top d-flex align-center ga-2 mb-2">
          Tags:
          <workflow-aggregates-tag-selector :object="object" @update:modelValue="markAsDirty" />
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-tooltip v-if="!isDiffMode" location="top" open-delay="500">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            style="border-radius: 0 !important;"
            variant="text"
            density="compact"
            class="text-none"
            :disabled="!object.isDirty || $store.state.isWorkflowDisabled"
            @click="emit('saveFile', object)"
          >
            <v-icon class="mr-2">mdi-content-save</v-icon> Save
          </v-btn>
        </template>
        <span>Save changes Ctrl/Cmd+S</span>
      </v-tooltip>
      <v-tooltip v-if="isDiffMode" location="top" open-delay="500">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            style="border-radius: 0 !important;"
            variant="text"
            density="compact"
            class="text-none"
            :disabled="$store.state.isWorkflowDisabled"
            @click="applyChanges(object)"
          >
            <v-icon class="mr-2">mdi-file-compare</v-icon> Apply
          </v-btn>
        </template>
        <span>Apply patches Ctrl/Cmd+P</span>
      </v-tooltip>
      <v-tooltip v-if="isDiffMode" location="top" open-delay="500">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            style="border-radius: 0 !important;"
            variant="text"
            density="compact"
            class="text-none"
            :disabled="!object.isDirty || $store.state.isWorkflowDisabled"
            @click="emit('saveFile', object)"
          >
            <v-icon class="mr-2">mdi-content-save-edit</v-icon> Save
          </v-btn>
        </template>
        <span>Save patches Ctrl/Cmd+S</span>
      </v-tooltip>
      <template v-if="!isPromptFile">
        <v-tooltip location="top" open-delay="500">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              style="border-radius: 0 !important;"
              variant="text"
              density="compact"
              class="text-none"
              :disabled="$store.state.isWorkflowDisabled"
              @click="emit('deleteFile', object)"
            >
              <v-icon class="mr-2">mdi-delete-outline</v-icon> Delete
            </v-btn>
          </template>
          <span>Delete</span>
        </v-tooltip>
      </template>
      <template v-else>
        <v-tooltip location="top" open-delay="500">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              style="border-radius: 0 !important;"
              variant="text"
              density="compact"
              class="text-none"
              :disabled="object.contents === originalTemplateContent || $store.state.isWorkflowDisabled"
              @click="revertChanges"
            >
              <v-icon class="mr-2">mdi-undo-variant</v-icon> Revert
            </v-btn>
          </template>
          <span>Revert to original prompt</span>
        </v-tooltip>
      </template>
    </div>
    <code-diff-viewer
      v-if="isDiffMode"
      v-model:original="object.contents"
      v-model="object.draftContents"
      v-model:isDirty="object.isDirty"
      :readOnly="readOnly"
      @save="emit('saveFile', object)"
      :language="language"
    />
    <code-editor
      v-else
      v-model="object.contents"
      v-model:isDirty="object.isDirty"
      :readOnly="readOnly"
      @save="emit('saveFile', object)"
      :language="language"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import CodeEditor from '@/components/CodeEditor.vue'
import CodeDiffViewer from '@/components/CodeDiffViewer.vue'
import WorkflowAggregatesTagSelector from '@/components/WorkflowAggregatesTagSelector.vue'
import { getLanguageFromFilename } from '@/codegen/utils'
import {
  AI_USER_PROMPT_ENTITY_NODEJS,
  AI_USER_PROMPT_COMMAND_NODEJS,
  AI_USER_PROMPT_READMODEL_NODEJS,
  AI_USER_PROMPT_GWT_NODEJS
} from '@/shared/prompts'

const { object, readOnly } = defineProps({
  object: {
    type: Object,
    required: true
  },
  readOnly: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['saveFile', 'deleteFile', 'update:object', 'regeneratePrompt'])

const isDiffMode = computed(() => object.draftContents !== null && object.draftContents !== undefined)
const language = computed(() => getLanguageFromFilename(object.filepath))
const isPromptFile = computed(() => {
  return object.filepath.startsWith('root/.qlerify/')
})
const originalTemplateContent = computed(() => {
  const filepathParts = object.filepath.split('/')
  if (!filepathParts.length) {
    return null
  }

  const filename = filepathParts.pop().replace('.md', '')
  const parts = filename.split('-')
  const type = parts.shift()

  const promptMap = {
    entity: AI_USER_PROMPT_ENTITY_NODEJS,
    command: AI_USER_PROMPT_COMMAND_NODEJS,
    readmodel: AI_USER_PROMPT_READMODEL_NODEJS,
    gwt: AI_USER_PROMPT_GWT_NODEJS
  }

  return promptMap[type] || null
})

const applyChanges = () => {
  const updateObject = {
    ...object,
    contents: object.draftContents,
    draftContents: null
  }
  emit('saveFile', updateObject)
}

const markAsDirty = () => {
  const updatedObject = { ...object, isDirty: true }
  emit('update:object', updatedObject)
}

const revertChanges = () => {
  if (!isPromptFile.value) {
    return
  }

  const updatedObject = {
    ...object,
    contents: originalTemplateContent.value,
    draftContents: null,
    isDirty: false
  }
  emit('saveFile', updatedObject)
}
</script>

<style scoped>
.toolbar-top {
  font-weight: 500;
  padding: 2px 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
